import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import {
  ChevronLeft, ChevronRight, Download, Presentation, 
  Shield, Bug, Cloud, FileSearch, Users, Award, 
  Globe, Layers, CheckCircle2, Target, Zap, Lock,
  BarChart3, ArrowRight, Mail, Building2,
  Maximize2, Minimize2, Play, Pause, FileText, Key,
  Database, AlertTriangle, TrendingUp, Activity, Cpu,
  Network, Eye, Search, Radio, Calculator, Newspaper,
  Server, Heart, Landmark, Factory, MapPin, Clock,
  Sparkles, Brain, Wifi, ShieldAlert, Crosshair,
  Smartphone, Monitor, FileCode, Bot, DollarSign,
  ClipboardCheck, Scale, Scan, Package, Home, Fingerprint
} from 'lucide-react';
import { toast } from 'sonner';

// ── Slide Data ─────────────────────────────────────────────────────────
const PRESENTATION_SLIDES = [
  // 1 — Title
  {
    id: 'title',
    type: 'title',
    title: 'Beskar IT',
    subtitle: 'Security. Privacy. Compliance. One platform.',
    tagline: '"This is the way."',
    catchline: 'Stop juggling 10+ security tools. One platform for GRC, pentesting, cloud security, threat intel, and India DPDPA compliance.',
    description: 'Trusted by CISOs, DPOs, and security teams to reduce audit prep by 70%, detect PII across databases, and stay compliant with 50+ frameworks.',
    version: 'v3.0.0 — April 2026',
    backgroundGradient: 'from-slate-950 via-indigo-950 to-slate-950'
  },

  // 2 — The Problem
  {
    id: 'the-problem',
    type: 'problem-slide',
    title: 'The Cost of Fragmented Security',
    subtitle: 'What keeps CISOs and DPOs up at night',
    problems: [
      { stat: '$4.45M', label: 'Average data breach cost', source: 'IBM Security 2023', icon: DollarSign, color: 'red' },
      { stat: '10-50+', label: 'Security tools per organization', source: 'Ponemon Institute', icon: Layers, color: 'orange' },
      { stat: '207 days', label: 'Mean time to identify a breach', source: 'IBM MTTR', icon: Clock, color: 'amber' },
      { stat: '72 hours', label: 'DPDPA DPB notification deadline', source: 'DPDPA Section 8(6)', icon: AlertTriangle, color: 'rose' },
      { stat: '$3.5M', label: 'Annual compliance spend', source: 'Deloitte', icon: Scale, color: 'purple' },
      { stat: '70%', label: 'Threat intel never seen', source: 'Gartner', icon: Eye, color: 'blue' }
    ],
    backgroundGradient: 'from-red-950 via-rose-950 to-slate-950'
  },

  // 3 — Platform Overview (6 products)
  {
    id: 'platform-overview',
    type: 'platform-overview',
    title: 'Beskar Security Platform',
    subtitle: 'Six integrated modules. One operating model. Zero tool sprawl.',
    description: 'Purpose-built for CISOs, DPOs, compliance officers, security engineers, threat hunters, and auditors.',
    products: [
      { name: 'GRC Nexus', tagline: 'Compliance & Risk', icon: 'shield', color: 'blue', description: 'Multi-framework compliance, risk heatmaps, audit lifecycle, and evidence management.' },
      { name: 'LiteSabre', tagline: 'Vuln Scanner', icon: 'bug', color: 'green', description: '176+ rules for code, containers, IaC, and secrets. SBOM generation.' },
      { name: 'LiteSabre Pro', tagline: 'AI Pentest Engine', icon: 'crosshair', color: 'red', description: 'Autonomous AI pentesting, 350+ payloads, STRIDE threat modeling.' },
      { name: 'Amidala CSPM', tagline: 'Cloud Security', icon: 'cloud', color: 'cyan', description: '185 controls across AWS, Azure, GCP with automated evidence collection.' },
      { name: 'OSINT Toolkit', tagline: 'Threat Intel', icon: 'search', color: 'purple', description: '15+ intel sources, IOC analysis, email forensics, real-time newsfeed.' },
      { name: 'DPDPA Manager', tagline: 'Data Privacy', icon: 'fingerprint', color: 'rose', description: 'India DPDPA 2023 compliance: consent, DSR, PII scanning, breach tracking.' }
    ],
    metrics: [
      { value: '70%', label: 'Audit prep time reduction' },
      { value: '6', label: 'Integrated security modules' },
      { value: '50+', label: 'Compliance frameworks' },
      { value: '72h', label: 'Breach notification tracking' }
    ],
    backgroundGradient: 'from-slate-950 to-indigo-950'
  },

  // 4 — GRC Nexus
  {
    id: 'grc-nexus',
    type: 'grc-detail',
    title: 'GRC Nexus',
    subtitle: 'Compliance, Risk & Audit — Unified',
    description: 'Manage ISO 27001, SOC 2, GDPR, HIPAA, PCI DSS, NIST, DPDPA, and 50+ frameworks from a single control set. Cross-map controls, reuse evidence, and cut duplicate work.',
    features: [
      { title: '50+ Frameworks, One View', description: 'Map overlapping controls once. Audit evidence reused across every framework.', icon: Shield },
      { title: 'Risk Heatmaps & KRIs', description: 'Interactive 5x5 matrix, inherent vs residual risk, green/amber/red thresholds.', icon: AlertTriangle },
      { title: 'Full Audit Lifecycle', description: 'Engagements, findings, workpapers, calendar. Internal, external, SOC 2, ISO, HIPAA.', icon: ClipboardCheck },
      { title: 'Policy & Evidence Vault', description: 'Rich text editor, version history, approval workflows, evidence linking to controls.', icon: FileText },
      { title: 'Executive Dashboard', description: 'Real-time compliance posture across all frameworks, domains, and risk categories.', icon: BarChart3 },
      { title: 'STRIDE Threat Modeling', description: 'Drag-and-drop data flow diagrams with React Flow for STRIDE threat identification.', icon: Target }
    ],
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
    color: 'blue',
    backgroundGradient: 'from-blue-950 to-cyan-950'
  },

  // 4 — GRC Dashboard Demo
  {
    id: 'grc-dashboard-demo',
    type: 'demo-screenshot',
    title: 'GRC Dashboard',
    subtitle: 'Real-time Compliance Visibility',
    screenshotType: 'grc',
    liveStats: [
      { label: 'Total controls in scope', value: '185+' },
      { label: 'Overall compliance rate', value: '87%' },
      { label: 'Items pending review', value: '8' },
      { label: 'Supporting documents', value: '156' }
    ],
    frameworkPosture: [
      { name: 'ISO 27001', score: '92%' },
      { name: 'SOC 2', score: '88%' },
      { name: 'GDPR', score: '85%' },
      { name: 'HIPAA', score: '78%' }
    ],
    description: 'Executives and auditors see where you are strong, where you are exposed, and which actions are already in progress.',
    features: ['Framework compliance scores', 'Risk heatmap overview', 'Document approval workflows', 'Evidence collection tracking'],
    backgroundGradient: 'from-blue-800 to-indigo-900'
  },

  // 5 — Risk & Audit Management (NEW)
  {
    id: 'risk-audit',
    type: 'risk-audit',
    title: 'Risk & Audit Management',
    subtitle: 'Complete Risk Visibility and Audit Lifecycle',
    description: 'Integrated risk assessment with interactive heatmaps, KRI monitoring, and full audit lifecycle management from engagement to finding to workpaper.',
    riskFeatures: [
      { title: 'Risk Register', description: 'Categorize and track risks with likelihood × impact scoring', icon: AlertTriangle },
      { title: '5×5 Risk Heatmap', description: 'Interactive visual risk matrix with inherent vs residual plotting', icon: Target },
      { title: 'Key Risk Indicators', description: 'Green / Amber / Red threshold monitoring with trend tracking', icon: TrendingUp },
      { title: 'Risk Treatments', description: 'Mitigate, Accept, Transfer, or Avoid — with tracking', icon: Shield }
    ],
    auditFeatures: [
      { title: 'Audit Engagements', description: 'Internal, External, SOC 2, ISO, PCI-DSS, HIPAA, GDPR audit types', icon: ClipboardCheck },
      { title: 'Findings & Severity', description: 'Critical / High / Medium / Low / Informational with remediation', icon: Search },
      { title: 'Workpapers', description: 'Testing documentation, evidence screenshots, review sign-off', icon: FileText },
      { title: 'Audit Calendar', description: 'Annual planning and schedule visualization', icon: Clock }
    ],
    backgroundGradient: 'from-amber-900 via-orange-900 to-red-900'
  },

  // 6 — OSINT Toolkit
  {
    id: 'osint-toolkit',
    type: 'osint-detail',
    title: 'Beskar OSINT Toolkit',
    subtitle: 'Open Source Intelligence Platform',
    description: 'End-to-end OSINT collection, enrichment and scoring with integrated CVSS 3.1 analytics.',
    features: [
      { title: 'IOC Analyzer', description: 'Enrich IPv4/IPv6, domains, URLs and hashes against 15+ intel sources.', icon: Search },
      { title: 'Email Analyzer', description: 'Extract indicators, validate SPF/DKIM/DMARC and inspect attachments/URLs.', icon: Mail },
      { title: 'IOC Extractor', description: 'Bulk extraction of IPs, domains, URLs, emails and hashes from raw text or files.', icon: FileSearch },
      { title: 'Cybersecurity Newsfeed', description: 'Aggregated, de-duplicated feed from 16 trusted security news sources.', icon: Newspaper },
      { title: 'CVSS 3.1 Calculator', description: 'Base, temporal and environmental scoring for risk-aligned decisions.', icon: Calculator },
      { title: 'Reputation Color Coding', description: 'Intuitive red/amber/green threat levels for rapid triage.', icon: Eye }
    ],
    image: 'https://images.unsplash.com/photo-1675627453084-505806a00406?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
    color: 'purple',
    backgroundGradient: 'from-purple-900 to-pink-900'
  },

  // 7 — OSINT Demo
  {
    id: 'osint-demo',
    type: 'demo-screenshot',
    title: 'OSINT Analysis — Live View',
    subtitle: 'Threat Intelligence Analysis',
    screenshotType: 'osint',
    sampleInvestigation: [
      { indicator: 'IP 192.168.1.100', risk: 'High risk', color: 'red' },
      { indicator: 'Domain malware-c2.evil.com', risk: 'Critical C2 infrastructure', color: 'red' },
      { indicator: 'Hash a1b2c3d4e5f6...', risk: 'Medium, observed in recent campaigns', color: 'yellow' },
      { indicator: 'CVE CVE-2024-1234', risk: 'High severity vulnerability', color: 'orange' }
    ],
    description: 'The analyst sees enrichment, context and risk in seconds instead of manually pivoting across multiple tools.',
    features: ['IOC reputation lookup', 'Email header analysis', 'Threat intelligence feeds', 'CVSS score calculation'],
    backgroundGradient: 'from-purple-800 to-violet-900'
  },

  // 8 — LiteSabre Scanner
  {
    id: 'litesabre',
    type: 'litesabre-detail',
    title: 'LiteSabre Scanner',
    subtitle: 'Vulnerability, Secret & IaC Scanner',
    description: 'Unified scanning for applications, containers and infrastructure with 176+ rules and real vulnerability database integration.',
    features: [
      { title: 'Multi-Type Scanning', description: 'Dependencies, Git repositories, Docker images and Terraform/K8s/Dockerfile IaC.', icon: Bug },
      { title: 'Secret Detection (40 patterns)', description: 'AWS, GCP, Azure, GitHub, Slack, Stripe, private keys, AI services and more.', icon: Key },
      { title: 'Security Rules Library (176+)', description: 'SAST, API, crypto, auth, serialization, IaC, frontend, headers.', icon: Shield },
      { title: 'Real Vulnerability Intelligence', description: 'Live enrichment from NVD and OSV APIs with CVSS scoring.', icon: Database },
      { title: 'SBOM Generation', description: 'CycloneDX, SPDX, CSV and JSON with component health summaries.', icon: Package },
      { title: 'CI/CD Integration', description: 'GitHub Actions, GitLab CI, Jenkins — auto-fail on severity thresholds.', icon: Zap }
    ],
    image: 'https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
    color: 'green',
    backgroundGradient: 'from-green-900 to-emerald-900'
  },

  // 9 — LiteSabre Demo
  {
    id: 'litesabre-demo',
    type: 'demo-screenshot',
    title: 'Vulnerability Detection — Live View',
    subtitle: 'Current Findings',
    screenshotType: 'litesabre',
    findings: [
      { severity: 'Critical', count: '3', color: 'red' },
      { severity: 'High', count: '12', color: 'orange' },
      { severity: 'Medium', count: '28', color: 'yellow' },
      { severity: 'Low', count: '45', color: 'blue' }
    ],
    secretsDetected: ['AWS keys', 'Private keys', 'API tokens', 'Database credentials'],
    description: 'Mapped directly into remediation workflows and compliance evidence so fixes and proof travel together.',
    features: ['176+ security rules', 'Secret detection alerts', 'CI/CD pipeline blocking', 'PDF & JSON reports'],
    backgroundGradient: 'from-green-800 to-teal-900'
  },

  // 10 — LiteSabre Pro (NEW)
  {
    id: 'litesabre-pro',
    type: 'litesabre-pro-detail',
    title: 'LiteSabre Pro',
    subtitle: 'Automated Penetration Testing',
    description: 'Enterprise-grade automated security testing for web, mobile and desktop applications with 350+ attack payloads, 44 security rules and STRIDE threat modeling across OWASP categories.',
    scanTypes: [
      { name: 'Web DAST', description: 'Full OWASP Top 10 testing with fuzzing', icon: Globe, payloads: '250+', color: 'red' },
      { name: 'Mobile', description: 'APK/IPA static analysis and secrets detection', icon: Smartphone, payloads: '20+', color: 'orange' },
      { name: 'Desktop', description: 'PE/ELF/Mach-O/DMG binary security analysis', icon: Monitor, payloads: '15+', color: 'amber' }
    ],
    attackCategories: [
      { name: 'SQL Injection', count: 55, color: 'red' },
      { name: 'XSS', count: 40, color: 'orange' },
      { name: 'Command Injection', count: 51, color: 'yellow' },
      { name: 'Path Traversal', count: 35, color: 'green' },
      { name: 'SSRF', count: 45, color: 'blue' },
      { name: 'SSTI', count: 24, color: 'purple' }
    ],
    vulnLibraries: 36,
    ecosystems: 5,
    inspirations: ['Intruder', 'Acunetix', 'Qualys', 'Picus', 'RidgeBot', 'SafeBreach'],
    backgroundGradient: 'from-red-900 via-rose-900 to-red-900'
  },

  // 11 — LiteSabre Pro Demo (NEW)
  {
    id: 'litesabre-pro-demo',
    type: 'demo-screenshot',
    title: 'LiteSabre Pro — Live View',
    subtitle: 'Automated Pentest Results',
    screenshotType: 'litesabre-pro',
    findings: [
      { severity: 'Critical', count: '2', color: 'red' },
      { severity: 'High', count: '7', color: 'orange' },
      { severity: 'Medium', count: '15', color: 'yellow' },
      { severity: 'Low', count: '22', color: 'blue' }
    ],
    description: 'Findings include OWASP Top 10 mapping, CVSS scores, exploitation examples, and Burp Suite-style PDF/HTML reports.',
    features: ['OWASP Top 10 mapping', '350+ attack payloads', 'STRIDE threat modeling', 'AI pentest agent'],
    backgroundGradient: 'from-red-800 to-rose-900'
  },

  // 12 — Amidala CSPM
  {
    id: 'amidala-cspm',
    type: 'cspm-detail',
    title: 'Amidala CSPM',
    subtitle: 'Cloud Security Posture Management',
    description: 'Continuous multi-cloud assessment across AWS, Azure and GCP with 185+ pre-built controls, 115 CSPM checks and compliance mappings.',
    features: [
      { title: 'Multi-Cloud Coverage', description: 'AWS (19 checks), Azure (12), GCP (15), tuned to native services.', icon: Cloud },
      { title: 'Scan Categories', description: 'IAM, storage, logging, networking, encryption, databases and more.', icon: Layers },
      { title: 'Compliance Mapping', description: 'Findings mapped to CIS, NIST 800-53, PCI DSS, SOC 2, GDPR, HIPAA, ISO 27001.', icon: Target },
      { title: 'Real-Time Notifications', description: 'WebSocket-driven job updates and security alerts.', icon: Radio },
      { title: 'Multi-Cloud Control Catalog', description: '29 security controls across AWS, Azure, GCP, On-Prem with YAML storage.', icon: Database },
      { title: 'Export & Reporting', description: 'JSON, CSV and HTML exports for audit readiness.', icon: Download }
    ],
    image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
    color: 'cyan',
    backgroundGradient: 'from-cyan-900 to-teal-900'
  },

  // 13 — CSPM Demo
  {
    id: 'cspm-demo',
    type: 'demo-screenshot',
    title: 'Cloud Security Assessment — Live View',
    subtitle: 'Current Cloud Posture',
    screenshotType: 'cspm',
    cloudPosture: [
      { cloud: 'AWS', score: '76%', findings: '24 open findings' },
      { cloud: 'Azure', score: '82%', findings: '18 open findings' },
      { cloud: 'GCP', score: '89%', findings: '8 open findings' }
    ],
    description: 'Teams see posture per cloud, track trending improvement and link misconfigurations back to specific controls.',
    features: ['Multi-cloud dashboard', '115 CSPM security checks', 'Compliance scoring', '185+ control catalog'],
    backgroundGradient: 'from-cyan-800 to-blue-900'
  },

  // 14 — DDoS Live Monitor
  {
    id: 'ddos-monitor',
    type: 'ddos-live',
    title: 'DDoS Live Monitor — LIVE',
    subtitle: 'Real-time Attack Detection & Response',
    description: 'Monitor distributed denial-of-service attacks in real-time with geographic visualization, traffic analysis, and automated mitigation recommendations.',
    stats: [
      { label: 'Total attacks (24h)', value: '847' },
      { label: 'Mitigated', value: '812' },
      { label: 'Peak traffic', value: '45.2 Gbps' },
      { label: 'Avg response time', value: '< 50 ms' }
    ],
    liveEvents: [
      { type: 'Mitigating', target: 'api.example.com', attack: 'SYN flood', origin: 'CN', pps: '2.4M PPS' },
      { type: 'Blocking', target: 'cdn.example.com', attack: 'UDP amplification', origin: 'RU', pps: '890K PPS' },
      { type: 'Active', target: 'www.example.com', attack: 'HTTP flood', origin: 'VN', pps: '450K PPS' },
      { type: 'Mitigating', target: 'dns.example.com', attack: 'DNS amplification', origin: 'UA', pps: '1.2M PPS' }
    ],
    backgroundGradient: 'from-red-900 via-orange-900 to-red-900'
  },

  // 15 — USPCF Framework
  {
    id: 'uspcf-framework',
    type: 'uspcf',
    title: 'USPCF — Unified Framework',
    subtitle: 'Patent Pending',
    description: 'Unified Security & Privacy Control Framework with 185+ controls across 14 domains mapped to HIPAA, PCI DSS, GDPR, ISO 27001, SOC 2 and NIST 800-53.',
    domains: [
      'Access Control', 'Incident Response', 'Risk Management', 'Cryptography',
      'Asset Management', 'Vendor Management', 'Business Continuity', 'Training & Awareness',
      'Audit & Monitoring', 'Privacy & DLP', 'Threat Intelligence'
    ],
    mappings: ['HIPAA', 'PCI DSS', 'GDPR', 'ISO 27001', 'SOC 2', 'NIST 800-53'],
    stats: { controls: 185, domains: 14, mappings: 6 },
    insight: 'This becomes the backbone that powers reduced audit time and consistent evidence reuse across frameworks.',
    backgroundGradient: 'from-amber-900 to-orange-900'
  },

  // 16 — Frameworks Grid
  {
    id: 'frameworks',
    type: 'frameworks-grid',
    title: '50+ Compliance Frameworks',
    subtitle: 'Comprehensive Coverage',
    description: 'All mapped back into the USPCF to minimize duplicate controls and conflicting requirements.',
    frameworkGroups: [
      { category: 'Information Security', frameworks: ['ISO 27001', 'ISO 27002', 'ISO 27017', 'ISO 27018', 'ISO 27701'], icon: Shield, color: 'blue' },
      { category: 'AI & Technology', frameworks: ['ISO 42001', 'NIST AI RMF', 'EU AI Act'], icon: Brain, color: 'purple' },
      { category: 'Privacy Regulations', frameworks: ['GDPR', 'CCPA/CPRA', 'DPDPA', 'LGPD', 'POPIA'], icon: Lock, color: 'green' },
      { category: 'Healthcare', frameworks: ['HIPAA', 'HITRUST', 'HITECH', 'NHS DSP'], icon: Heart, color: 'red' },
      { category: 'Financial Services', frameworks: ['PCI DSS 4.0', 'SOX', 'GLBA', 'FFIEC', 'MAS TRM'], icon: Landmark, color: 'amber' },
      { category: 'Government & Defense', frameworks: ['NIST 800-53', 'NIST CSF 2.0', 'FedRAMP', 'CMMC 2.0', 'CIS v8'], icon: Building2, color: 'slate' },
      { category: 'SOC Reports', frameworks: ['SOC 1 Type I/II', 'SOC 2 Type I/II', 'SOC 3'], icon: CheckCircle2, color: 'cyan' },
      { category: 'Regional Standards', frameworks: ['SAMA', 'NESA', 'PDPL', 'CBK'], icon: MapPin, color: 'orange' },
      { category: 'Industry Specific', frameworks: ['NERC CIP', 'IEC 62443', 'TISAX', 'CSA STAR'], icon: Factory, color: 'indigo' }
    ],
    backgroundGradient: 'from-indigo-900 to-blue-900'
  },

  // 17 — AI Roadmap (EXTENDED TO Q1 2027)
  {
    id: 'ai-roadmap',
    type: 'ai-roadmap',
    title: 'AI-Enhanced Roadmap',
    subtitle: 'Shannon-Inspired Autonomous Security',
    description: 'Inspired by Shannon — the autonomous AI pentester achieving 96.15% success rate. Bringing AI into every Beskar module through Q1 2027.',
    phases: [
      { version: 'v2.5', target: 'Q2 2026', name: 'AI Risk Scoring', description: 'ML-driven risk prioritization, automated threat correlation, intelligent KRI thresholds', icon: TrendingUp, color: 'green' },
      { version: 'v3.0', target: 'Q3 2026', name: 'AI Policy & Evidence', description: 'LLM-powered policy generation, automated evidence collection, gap analysis', icon: FileText, color: 'blue' },
      { version: 'v3.5', target: 'Q4 2026', name: 'AI VAPT Engine', description: 'Shannon-inspired autonomous pentesting: Recon → Vuln Analysis → Exploitation → Reporting', icon: Crosshair, color: 'red' },
      { version: 'v4.0', target: 'Q1 2027', name: 'AI Cloud Advisor', description: 'Intelligent remediation scripts, drift detection, blast radius analysis for Amidala CSPM', icon: Cloud, color: 'cyan' }
    ],
    shannonRef: 'github.com/KeygraphHQ/shannon',
    backgroundGradient: 'from-violet-900 via-purple-900 to-indigo-900'
  },

  // 18 — Extended Roadmap (Q2 2026 - Q1 2027)
  {
    id: 'extended-roadmap',
    type: 'extended-roadmap',
    title: 'Product Roadmap 2026-2027',
    subtitle: 'Quarterly Milestones Through Q1 2027',
    description: 'Strategic feature releases aligned with market demands and customer feedback.',
    quarters: [
      { 
        quarter: 'Q2 2026', 
        theme: 'Foundation & AI',
        features: ['AI Risk Scoring Engine', 'USPCF Control Mapping UI', 'Enhanced Vendor Workflows', 'Mobile-Responsive Dashboard'],
        color: 'green'
      },
      { 
        quarter: 'Q3 2026', 
        theme: 'Intelligence & Automation',
        features: ['AI Policy Generator', 'Automated Evidence Collection', 'OSINT AI Correlation', 'Advanced SBOM Analytics'],
        color: 'blue'
      },
      { 
        quarter: 'Q4 2026', 
        theme: 'Autonomous Security',
        features: ['AI VAPT Engine (Shannon)', 'Predictive Threat Modeling', 'Multi-Language Support', 'SSO Enhancements'],
        color: 'purple'
      },
      { 
        quarter: 'Q1 2027', 
        theme: 'Enterprise Excellence',
        features: ['AI Cloud Advisor', 'Custom Report Builder', 'API Marketplace', 'WCAG 2.1 AA Compliance'],
        color: 'cyan'
      }
    ],
    backgroundGradient: 'from-slate-900 via-indigo-900 to-slate-900'
  },

  // 19 — Capabilities
  {
    id: 'capabilities',
    type: 'capabilities',
    title: 'Platform Capabilities',
    subtitle: 'Built for enterprise security teams',
    description: 'Measure impact through fewer audit days, reduced consulting spend and faster time-to-attestation.',
    capabilities: [
      { value: '70%', label: 'Audit time reduction', description: 'Through unified controls and reusable evidence', icon: Clock },
      { value: '176+', label: 'Security rules', description: 'For vulnerabilities, secrets, IaC and APIs', icon: Shield },
      { value: '250+', label: 'Attack payloads', description: 'For automated penetration testing', icon: Crosshair },
      { value: '50+', label: 'Compliance frameworks', description: 'Across 9 industry categories', icon: CheckCircle2 }
    ],
    backgroundGradient: 'from-slate-900 to-indigo-900'
  },

  // 20 — Use Cases
  {
    id: 'use-cases',
    type: 'use-cases',
    title: 'Proven Use Cases',
    subtitle: 'Real-World Results',
    useCases: [
      { title: 'Achieve DPDPA compliance in weeks', description: 'Consent management, PII scanning, 72h breach tracking, DPB letter generation', icon: Shield },
      { title: 'Detect PII across all databases', description: 'Scan PostgreSQL, MySQL, SQL Server, MongoDB across AWS, Azure, GCP', icon: Database },
      { title: 'Automate penetration testing with AI', description: 'Autonomous agent with 12+ Kali tools and HackerOne scope import', icon: Bot },
      { title: 'Maintain compliance across 50+ frameworks', description: 'With cross-framework mapping, reusable evidence, and audit lifecycle', icon: CheckCircle2 },
      { title: 'Reduce audit prep by 70%', description: 'Through unified controls, automated evidence collection, and CSPM integration', icon: Clock },
      { title: 'Deploy consent banners in minutes', description: 'Embeddable JS + React components with analytics and purpose tracking', icon: Globe }
    ],
    backgroundGradient: 'from-slate-950 via-purple-950 to-slate-950'
  },

  // 21 — DPDPA Consent Manager
  {
    id: 'dpdpa-manager',
    type: 'dpdpa-detail',
    title: 'DPDPA Consent Manager',
    subtitle: "India's Digital Personal Data Protection Act, 2023",
    description: 'The only platform that combines consent management, PII discovery, breach notification, and compliance reporting in a single module — purpose-built for DPDPA.',
    features: [
      { title: 'Consent Purpose Management', description: 'Define processing purposes with data categories, retention periods, and mandatory consent flags per DPDPA Section 6.', icon: FileText, color: 'blue' },
      { title: 'Consent Records & Tracking', description: 'Record, withdraw, and audit consents with full lifecycle tracking and legal basis documentation.', icon: CheckCircle2, color: 'green' },
      { title: 'Data Subject Requests (DSR)', description: '5 request types (access, correction, erasure, grievance, nomination) with 7-day SLA and Jira integration.', icon: Users, color: 'purple' },
      { title: 'Consent Banner Builder', description: 'Visual builder with live preview. Generate embeddable JS snippet AND React component. Public consent API.', icon: Globe, color: 'cyan' },
      { title: 'DPDPA Compliance Dashboard', description: 'Real-time 5-factor compliance score, consent stats, DSR metrics, PII scan summary, breach alerts.', icon: BarChart3, color: 'amber' },
      { title: 'PDF Compliance Report', description: 'DPO-ready export with compliance score breakdown, consent stats, DSR metrics, PII data map, audit trail.', icon: Download, color: 'rose' }
    ],
    stats: [
      { value: '14', label: 'PII categories detected' },
      { value: '5', label: 'DSR request types' },
      { value: '7 days', label: 'SLA tracking' },
      { value: 'PDF', label: 'DPO-ready reports' }
    ],
    backgroundGradient: 'from-rose-950 via-pink-950 to-purple-950'
  },

  // 22 — PII Scanner & Multi-DB
  {
    id: 'pii-scanner',
    type: 'pii-scanner-detail',
    title: 'PII Data Scanner',
    subtitle: 'Find personal data before regulators do',
    description: 'Scan internal and external databases for 14 categories of PII. Connect to AWS RDS, Azure SQL, GCP Cloud SQL, or on-prem databases. Automated DPDPA non-compliance analysis.',
    databases: [
      { name: 'PostgreSQL', cloud: 'AWS RDS / GCP Cloud SQL', color: 'blue' },
      { name: 'MySQL', cloud: 'AWS RDS / Azure', color: 'orange' },
      { name: 'SQL Server', cloud: 'Azure SQL', color: 'red' },
      { name: 'MongoDB', cloud: 'AWS DocumentDB / Azure Cosmos', color: 'green' }
    ],
    piiCategories: ['Aadhaar', 'PAN Card', 'Email', 'Phone', 'Full Name', 'Date of Birth', 'Address', 'IP Address', 'Financial Data', 'Health Data', 'Biometric', 'Password', 'Gender', 'Location'],
    complianceCheck: {
      title: 'DPDPA Non-Compliance Check',
      description: 'Automated detection of Section 8(4) security violations, unprotected sensitive data, and retention policy issues with actionable recommendations.'
    },
    backgroundGradient: 'from-emerald-950 via-teal-950 to-cyan-950'
  },

  // 23 — Breach Notification Tracker
  {
    id: 'breach-tracker',
    type: 'breach-tracker-detail',
    title: '72-Hour Breach Tracker',
    subtitle: 'DPDPA Section 8(6) — Never miss a deadline',
    description: 'Register data breaches, track the 72-hour DPB notification deadline in real-time, and auto-generate formal notification letters for the Data Protection Board and affected individuals.',
    countdownLevels: [
      { status: 'On Track', hours: '48-72h', color: 'green', description: 'Plenty of time to prepare notification' },
      { status: 'Warning', hours: '24-48h', color: 'amber', description: 'Prepare DPB letter now' },
      { status: 'Urgent', hours: '12-24h', color: 'orange', description: 'File DPB notification immediately' },
      { status: 'Critical', hours: '0-12h', color: 'red', description: 'Deadline imminent — escalate' },
      { status: 'Overdue', hours: 'Past due', color: 'rose', description: 'Regulatory risk — notify DPB now' }
    ],
    letters: [
      { name: 'DPB Notification Letter', description: 'Formal letter to Data Protection Board with breach details, DPDPA Section 8(6) reference, affected data categories, remediation steps, DPO contact', format: 'PDF' },
      { name: 'Data Principal Notice', description: 'Notification to affected individuals with breach description, their rights under DPDPA, remediation advice, DPO contact information', format: 'PDF' }
    ],
    workflow: ['Detected', 'Investigating', 'Contained', 'DPB Notified', 'Principals Notified', 'Resolved', 'Closed'],
    backgroundGradient: 'from-red-950 via-rose-950 to-orange-950'
  },

  // 24 — Why Beskar (Competitive Differentiation)
  {
    id: 'why-beskar',
    type: 'why-beskar',
    title: 'Why Beskar?',
    subtitle: 'What sets us apart',
    comparisons: [
      { category: 'Modules', beskar: '6 integrated modules', others: '1-2 point solutions', advantage: '6x broader coverage' },
      { category: 'Frameworks', beskar: '50+ compliance frameworks', others: '5-15 frameworks', advantage: '3x more coverage' },
      { category: 'DPDPA', beskar: 'Full DPDPA suite built-in', others: 'No DPDPA support', advantage: 'India-ready' },
      { category: 'PII Scanning', beskar: 'Multi-DB: PG, MySQL, MSSQL, MongoDB', others: 'Manual data mapping', advantage: 'Automated discovery' },
      { category: 'AI Pentesting', beskar: 'Autonomous AI agent with 12+ tools', others: 'Manual or basic scanning', advantage: 'AI-driven' },
      { category: 'Breach Tracking', beskar: '72h countdown + auto-generated letters', others: 'Spreadsheet tracking', advantage: 'Regulatory-ready' },
      { category: 'Consent Banners', beskar: 'Builder + JS + React embed + analytics', others: 'Third-party CMP needed', advantage: 'No extra tools' },
      { category: 'Cloud Security', beskar: '185 controls, AWS/Azure/GCP', others: 'Single-cloud focus', advantage: 'Multi-cloud' }
    ],
    backgroundGradient: 'from-indigo-950 via-violet-950 to-purple-950'
  },

  // 25 — ROI / Business Value
  {
    id: 'roi-value',
    type: 'roi-value',
    title: 'Return on Investment',
    subtitle: 'Measurable business impact from day one',
    metrics: [
      { before: '10+ security tools', after: '1 platform', savings: '40% tool cost reduction', icon: DollarSign, color: 'green' },
      { before: '12 weeks audit prep', after: '3.5 weeks', savings: '70% time saved', icon: Clock, color: 'blue' },
      { before: 'Manual PII mapping', after: 'Automated scanning', savings: '90% faster discovery', icon: Search, color: 'purple' },
      { before: 'Spreadsheet breach tracking', after: '72h countdown + auto-letters', savings: 'Zero missed deadlines', icon: AlertTriangle, color: 'red' },
      { before: 'Separate consent tools', after: 'Built-in banner + tracking', savings: 'One less vendor', icon: Globe, color: 'cyan' },
      { before: 'Manual pentesting', after: 'AI agent + 350 payloads', savings: '5x faster testing', icon: Bot, color: 'orange' }
    ],
    backgroundGradient: 'from-emerald-950 via-green-950 to-teal-950'
  },

  // 26 — Contact
  {
    id: 'contact',
    type: 'contact',
    title: 'Ready to unify your security?',
    subtitle: 'Start Your Journey',
    description: "Book a demo and see how Beskar replaces 10+ tools with one platform — GRC, pentesting, cloud security, threat intel, and India DPDPA compliance.",
    email: 'support@beskarit.com',
    benefits: [
      '6 integrated modules replacing 10+ security tools',
      'Full DPDPA compliance with 72h breach tracking and auto-generated DPB letters',
      'AI-powered pentesting with 350+ payloads and STRIDE threat modeling',
      '50+ compliance frameworks with 70% faster audit preparation',
      'Multi-DB PII scanning across PostgreSQL, MySQL, SQL Server, MongoDB',
      'Embeddable consent banners with analytics — no extra CMP needed'
    ],
    tagline: '"This is the way."',
    version: 'v3.0.0 — April 2026',
    backgroundGradient: 'from-indigo-950 via-purple-950 to-indigo-950'
  }
];

// ── Color Helpers ──────────────────────────────────────────────────────
const getColorClasses = (color) => {
  const colorMap = {
    blue:   { bg: 'bg-blue-500/20',   border: 'border-blue-500/30',   text: 'text-blue-400',   accent: 'bg-blue-500',   light: 'bg-blue-500/10' },
    purple: { bg: 'bg-purple-500/20', border: 'border-purple-500/30', text: 'text-purple-400', accent: 'bg-purple-500', light: 'bg-purple-500/10' },
    green:  { bg: 'bg-green-500/20',  border: 'border-green-500/30',  text: 'text-green-400',  accent: 'bg-green-500',  light: 'bg-green-500/10' },
    cyan:   { bg: 'bg-cyan-500/20',   border: 'border-cyan-500/30',   text: 'text-cyan-400',   accent: 'bg-cyan-500',   light: 'bg-cyan-500/10' },
    red:    { bg: 'bg-red-500/20',    border: 'border-red-500/30',    text: 'text-red-400',    accent: 'bg-red-500',    light: 'bg-red-500/10' },
    amber:  { bg: 'bg-amber-500/20',  border: 'border-amber-500/30',  text: 'text-amber-400',  accent: 'bg-amber-500',  light: 'bg-amber-500/10' },
    orange: { bg: 'bg-orange-500/20', border: 'border-orange-500/30', text: 'text-orange-400', accent: 'bg-orange-500', light: 'bg-orange-500/10' },
    slate:  { bg: 'bg-slate-500/20',  border: 'border-slate-500/30',  text: 'text-slate-400',  accent: 'bg-slate-500',  light: 'bg-slate-500/10' },
    indigo: { bg: 'bg-indigo-500/20', border: 'border-indigo-500/30', text: 'text-indigo-400', accent: 'bg-indigo-500', light: 'bg-indigo-500/10' },
    rose:   { bg: 'bg-rose-500/20',   border: 'border-rose-500/30',   text: 'text-rose-400',   accent: 'bg-rose-500',   light: 'bg-rose-500/10' }
  };
  return colorMap[color] || colorMap.blue;
};

// ── Slide Components ───────────────────────────────────────────────────

// Title Slide
const TitleSlide = ({ slide }) => (
  <div className={`relative w-full h-full bg-gradient-to-br ${slide.backgroundGradient} flex flex-col items-center justify-center p-8 overflow-hidden`}>
    <div className="absolute inset-0 opacity-10">
      <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
    </div>
    <div className="mb-4 p-4 bg-white/10 rounded-2xl backdrop-blur-sm border border-white/20">
      <Shield className="w-16 h-16 text-blue-400" />
    </div>
    <h1 className="text-6xl font-bold text-white mb-3 tracking-tight">{slide.title}</h1>
    <p className="text-xl text-blue-300 mb-3 max-w-3xl text-center">{slide.subtitle}</p>
    <p className="text-lg text-gray-300 mb-3 max-w-2xl text-center italic">{slide.catchline}</p>
    <p className="text-3xl text-amber-400 font-semibold italic mb-4">{slide.tagline}</p>
    <p className="text-base text-gray-400 max-w-2xl text-center">{slide.description}</p>
    <Badge className="mt-4 bg-blue-500/20 text-blue-300 border-blue-500/30 text-sm">{slide.version}</Badge>
    <div className="absolute bottom-8 flex gap-2">
      <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
      <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse delay-100" />
      <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse delay-150" />
      <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse delay-200" />
    </div>
  </div>
);

// Platform Overview
const PlatformOverviewSlide = ({ slide }) => (
  <div className={`relative w-full h-full bg-gradient-to-br ${slide.backgroundGradient} flex flex-col p-6 overflow-hidden`}>
    <div className="text-center mb-4">
      <h2 className="text-3xl font-bold text-white mb-1">{slide.title}</h2>
      <p className="text-lg text-blue-300">{slide.subtitle}</p>
      <p className="text-gray-400 mt-1 max-w-3xl mx-auto text-sm">{slide.description}</p>
    </div>
    <div className="flex-1 grid grid-cols-3 md:grid-cols-6 gap-3 mb-4">
      {slide.products.map((product, i) => {
        const colors = getColorClasses(product.color);
        const icons = { shield: Shield, search: FileSearch, bug: Bug, cloud: Cloud, crosshair: Crosshair, fingerprint: Fingerprint };
        const Icon = icons[product.icon] || Shield;
        return (
          <div key={i} className={`${colors.bg} ${colors.border} border rounded-xl p-4 flex flex-col items-center justify-center backdrop-blur-sm hover:scale-105 transition-transform`}>
            <div className={`p-2 ${colors.bg} rounded-xl mb-2`}>
              <Icon className={`w-8 h-8 ${colors.text}`} />
            </div>
            <h3 className="text-base font-semibold text-white text-center">{product.name}</h3>
            <p className="text-xs text-gray-400 mb-2">{product.tagline}</p>
            <p className="text-xs text-gray-500 text-center leading-snug">{product.description}</p>
          </div>
        );
      })}
    </div>
    <div className="grid grid-cols-4 gap-3">
      {slide.metrics.map((m, i) => (
        <div key={i} className="bg-white/5 rounded-lg p-3 text-center border border-white/10">
          <p className="text-2xl font-bold text-white">{m.value}</p>
          <p className="text-xs text-gray-400">{m.label}</p>
        </div>
      ))}
    </div>
  </div>
);

// GRC / OSINT / LiteSabre / CSPM Detail Slide
const GRCDetailSlide = ({ slide }) => {
  const colors = getColorClasses(slide.color);
  return (
    <div className={`relative w-full h-full bg-gradient-to-br ${slide.backgroundGradient} flex p-6 overflow-hidden`}>
      <div className="flex-1 flex flex-col justify-center pr-4">
        <Badge className={`${colors.bg} ${colors.text} border-none mb-3 w-fit`}>{slide.subtitle}</Badge>
        <h2 className="text-4xl font-bold text-white mb-2">{slide.title}</h2>
        <p className="text-gray-300 mb-4 text-sm leading-relaxed">{slide.description}</p>
        <div className="grid grid-cols-2 gap-2">
          {slide.features.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <div key={i} className={`flex items-start gap-2 p-2 ${colors.light} rounded-lg border ${colors.border}`}>
                <Icon className={`w-4 h-4 ${colors.text} mt-0.5 flex-shrink-0`} />
                <div>
                  <span className="text-white text-sm font-medium">{feature.title}</span>
                  <p className="text-gray-400 text-xs">{feature.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="w-2/5 flex items-center">
        <div className={`rounded-2xl overflow-hidden border-2 ${colors.border} shadow-2xl`}>
          <img src={slide.image} alt={slide.title} className="w-full h-full object-cover" />
        </div>
      </div>
    </div>
  );
};

// Demo Screenshot Slide
const DemoScreenshotSlide = ({ slide }) => {
  const screenshots = {
    grc: {
      title: 'Executive Dashboard',
      metrics: [
        { label: 'Total Controls', value: '63', trend: '+5%' },
        { label: 'Compliance Rate', value: '87%', trend: '+12%' },
        { label: 'Pending Reviews', value: '8', trend: '-3' },
        { label: 'Documents', value: '156', trend: '+24' }
      ],
      frameworks: [
        { name: 'ISO 27001', score: 92, color: 'blue' },
        { name: 'SOC 2', score: 88, color: 'green' },
        { name: 'GDPR', score: 85, color: 'purple' },
        { name: 'HIPAA', score: 78, color: 'red' }
      ]
    },
    osint: {
      title: 'Threat Intelligence Dashboard',
      iocs: [
        { type: 'IP', value: '192.168.1.100', reputation: 'high', source: 'AbuseIPDB' },
        { type: 'Domain', value: 'malware-c2.evil.com', reputation: 'critical', source: 'VirusTotal' },
        { type: 'Hash', value: 'a1b2c3d4...', reputation: 'medium', source: 'Shodan' },
        { type: 'CVE', value: 'CVE-2024-1234', reputation: 'high', source: 'NVD' }
      ]
    },
    litesabre: {
      title: 'Vulnerability Scanner Results',
      findings: [
        { severity: 'critical', count: 3, color: 'red' },
        { severity: 'high', count: 12, color: 'orange' },
        { severity: 'medium', count: 28, color: 'yellow' },
        { severity: 'low', count: 45, color: 'blue' }
      ],
      secrets: ['AWS Key', 'Private Key', 'API Token', 'DB Password']
    },
    cspm: {
      title: 'Cloud Security Posture',
      clouds: [
        { name: 'AWS', score: 76, findings: 24, color: 'orange' },
        { name: 'Azure', score: 82, findings: 18, color: 'blue' },
        { name: 'GCP', score: 89, findings: 8, color: 'green' }
      ]
    },
    'litesabre-pro': {
      title: 'Automated Pentest Results',
      findings: [
        { severity: 'critical', count: 2, color: 'red' },
        { severity: 'high', count: 7, color: 'orange' },
        { severity: 'medium', count: 15, color: 'yellow' },
        { severity: 'low', count: 22, color: 'blue' }
      ],
      owaspCategories: [
        { id: 'A01', name: 'Broken Access Control', count: 4 },
        { id: 'A02', name: 'Cryptographic Failures', count: 3 },
        { id: 'A03', name: 'Injection', count: 8 },
        { id: 'A07', name: 'Auth Failures', count: 5 }
      ]
    }
  };

  const data = screenshots[slide.screenshotType] || screenshots.grc;

  return (
    <div className={`relative w-full h-full bg-gradient-to-br ${slide.backgroundGradient} flex flex-col p-6 overflow-hidden`}>
      <div className="text-center mb-4">
        <h2 className="text-3xl font-bold text-white mb-1">{slide.title}</h2>
        <p className="text-gray-300">{slide.subtitle}</p>
      </div>

      <div className="flex-1 bg-slate-900/80 rounded-xl border border-white/10 p-4 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
          <h3 className="text-lg font-semibold text-white">{data.title}</h3>
          <Badge className="bg-green-500/20 text-green-400">Live Data</Badge>
        </div>

        {slide.screenshotType === 'grc' && (
          <>
            <div className="grid grid-cols-4 gap-3 mb-4">
              {data.metrics.map((metric, i) => (
                <div key={i} className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-white">{metric.value}</p>
                  <p className="text-xs text-gray-400">{metric.label}</p>
                  <span className="text-xs text-green-400">{metric.trend}</span>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-4 gap-3">
              {data.frameworks.map((fw, i) => {
                const colors = getColorClasses(fw.color);
                return (
                  <div key={i} className={`${colors.light} rounded-lg p-3`}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white text-sm">{fw.name}</span>
                      <span className={`${colors.text} font-bold`}>{fw.score}%</span>
                    </div>
                    <Progress value={fw.score} className="h-2" />
                  </div>
                );
              })}
            </div>
          </>
        )}

        {slide.screenshotType === 'osint' && (
          <div className="space-y-2">
            {data.iocs.map((ioc, i) => {
              const repColors = { critical: 'red', high: 'orange', medium: 'yellow', low: 'green' };
              const colors = getColorClasses(repColors[ioc.reputation]);
              return (
                <div key={i} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <Badge className={`${colors.bg} ${colors.text}`}>{ioc.type}</Badge>
                    <span className="text-white font-mono text-sm">{ioc.value}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-gray-400 text-sm">{ioc.source}</span>
                    <Badge className={`${colors.bg} ${colors.text}`}>{ioc.reputation}</Badge>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {slide.screenshotType === 'litesabre' && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="text-white font-medium mb-3">Vulnerability Distribution</h4>
              <div className="space-y-2">
                {data.findings.map((f, i) => {
                  const colors = getColorClasses(f.color);
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded ${colors.accent}`} />
                      <span className="text-gray-300 capitalize">{f.severity}</span>
                      <span className="text-white font-bold ml-auto">{f.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <h4 className="text-white font-medium mb-3">Secrets Detected</h4>
              <div className="space-y-2">
                {data.secrets.map((secret, i) => (
                  <div key={i} className="flex items-center gap-2 bg-red-500/10 rounded-lg p-2">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                    <span className="text-red-300 text-sm">{secret}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {slide.screenshotType === 'cspm' && (
          <div className="grid grid-cols-3 gap-4">
            {data.clouds.map((cloud, i) => {
              const colors = getColorClasses(cloud.color);
              return (
                <div key={i} className={`${colors.light} rounded-xl p-4`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Cloud className={`w-6 h-6 ${colors.text}`} />
                    <span className="text-white font-semibold">{cloud.name}</span>
                  </div>
                  <div className="text-center mb-3">
                    <span className="text-4xl font-bold text-white">{cloud.score}%</span>
                    <p className="text-gray-400 text-sm">Security Score</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-2 text-center">
                    <span className="text-orange-400 font-bold">{cloud.findings}</span>
                    <span className="text-gray-400 text-sm ml-1">findings</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {slide.screenshotType === 'litesabre-pro' && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="text-white font-medium mb-3">Findings by Severity</h4>
              <div className="space-y-2">
                {data.findings.map((f, i) => {
                  const colors = getColorClasses(f.color);
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded ${colors.accent}`} />
                      <span className="text-gray-300 capitalize">{f.severity}</span>
                      <span className="text-white font-bold ml-auto">{f.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <h4 className="text-white font-medium mb-3">OWASP Top 10 Mapping</h4>
              <div className="space-y-2">
                {data.owaspCategories.map((cat, i) => (
                  <div key={i} className="flex items-center justify-between bg-white/5 rounded-lg p-2">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-red-500/20 text-red-400 text-xs">{cat.id}</Badge>
                      <span className="text-gray-300 text-sm">{cat.name}</span>
                    </div>
                    <span className="text-white font-bold">{cat.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2 mt-3 justify-center">
        {slide.features.map((feature, i) => (
          <Badge key={i} variant="outline" className="border-white/20 text-gray-300 text-xs">{feature}</Badge>
        ))}
      </div>
    </div>
  );
};

// Risk & Audit Slide (NEW)
const RiskAuditSlide = ({ slide }) => (
  <div className={`relative w-full h-full bg-gradient-to-br ${slide.backgroundGradient} flex flex-col p-6 overflow-hidden`}>
    <div className="text-center mb-4">
      <h2 className="text-3xl font-bold text-white mb-1">{slide.title}</h2>
      <p className="text-orange-300">{slide.subtitle}</p>
      <p className="text-gray-400 text-sm mt-1 max-w-2xl mx-auto">{slide.description}</p>
    </div>
    <div className="flex-1 grid grid-cols-2 gap-4">
      {/* Risk Column */}
      <div className="bg-white/5 rounded-xl border border-amber-500/30 p-4">
        <h3 className="text-lg font-semibold text-amber-400 mb-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" /> Risk Management
        </h3>
        <div className="space-y-2">
          {slide.riskFeatures.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="flex items-start gap-3 p-2 bg-amber-500/10 rounded-lg">
                <Icon className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="text-white text-sm font-medium">{f.title}</span>
                  <p className="text-gray-400 text-xs">{f.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Audit Column */}
      <div className="bg-white/5 rounded-xl border border-orange-500/30 p-4">
        <h3 className="text-lg font-semibold text-orange-400 mb-3 flex items-center gap-2">
          <ClipboardCheck className="w-5 h-5" /> Audit Management
        </h3>
        <div className="space-y-2">
          {slide.auditFeatures.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="flex items-start gap-3 p-2 bg-orange-500/10 rounded-lg">
                <Icon className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="text-white text-sm font-medium">{f.title}</span>
                  <p className="text-gray-400 text-xs">{f.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </div>
);

// LiteSabre Pro Detail Slide (NEW)
const LiteSabreProSlide = ({ slide }) => (
  <div className={`relative w-full h-full bg-gradient-to-br ${slide.backgroundGradient} flex flex-col p-6 overflow-hidden`}>
    <div className="text-center mb-3">
      <Badge className="bg-red-500/20 text-red-400 border-none mb-2">Enterprise Pentesting</Badge>
      <h2 className="text-3xl font-bold text-white mb-1">{slide.title}</h2>
      <p className="text-rose-300 text-sm">{slide.subtitle}</p>
      <p className="text-gray-400 text-xs mt-1 max-w-3xl mx-auto">{slide.description}</p>
    </div>

    <div className="flex-1 grid grid-cols-3 gap-3 mb-3">
      {slide.scanTypes.map((st, i) => {
        const colors = getColorClasses(st.color);
        const Icon = st.icon;
        return (
          <div key={i} className={`${colors.light} ${colors.border} border rounded-xl p-4 flex flex-col items-center text-center`}>
            <Icon className={`w-8 h-8 ${colors.text} mb-2`} />
            <h3 className="text-white font-semibold">{st.name}</h3>
            <p className="text-gray-400 text-xs mb-2">{st.description}</p>
            <Badge className={`${colors.bg} ${colors.text}`}>{st.payloads} checks</Badge>
          </div>
        );
      })}
    </div>

    <div className="grid grid-cols-6 gap-2 mb-3">
      {slide.attackCategories.map((cat, i) => {
        const colors = getColorClasses(cat.color);
        return (
          <div key={i} className={`${colors.light} rounded-lg p-2 text-center`}>
            <p className="text-lg font-bold text-white">{cat.count}</p>
            <p className="text-xs text-gray-400">{cat.name}</p>
          </div>
        );
      })}
    </div>

    <div className="flex items-center justify-center gap-6 text-sm">
      <div className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-1">
        <Database className="w-4 h-4 text-rose-400" />
        <span className="text-gray-300">{slide.vulnLibraries} Vulnerable Libraries across {slide.ecosystems} ecosystems</span>
      </div>
      <div className="flex items-center gap-1">
        <span className="text-gray-500 text-xs">Inspired by:</span>
        {slide.inspirations.map((name, i) => (
          <Badge key={i} variant="outline" className="border-white/10 text-gray-400 text-xs py-0">{name}</Badge>
        ))}
      </div>
    </div>
  </div>
);

// Extended Roadmap Slide (NEW)
const ExtendedRoadmapSlide = ({ slide }) => (
  <div className={`relative w-full h-full bg-gradient-to-br ${slide.backgroundGradient} flex flex-col p-6 overflow-hidden`}>
    <div className="absolute inset-0 opacity-5">
      <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '30px 30px' }} />
    </div>
    <div className="text-center mb-4">
      <Badge className="bg-indigo-500/20 text-indigo-400 border-none mb-2">Strategic Vision</Badge>
      <h2 className="text-3xl font-bold text-white mb-1">{slide.title}</h2>
      <p className="text-indigo-300 text-sm">{slide.subtitle}</p>
      <p className="text-gray-400 text-xs mt-1 max-w-2xl mx-auto">{slide.description}</p>
    </div>

    <div className="flex-1 grid grid-cols-4 gap-4">
      {slide.quarters.map((q, i) => {
        const colors = getColorClasses(q.color);
        return (
          <div key={i} className={`${colors.light} ${colors.border} border rounded-xl p-4 flex flex-col`}>
            <div className="flex items-center justify-between mb-3">
              <Badge className={`${colors.bg} ${colors.text}`}>{q.quarter}</Badge>
            </div>
            <h3 className="text-white font-semibold text-sm mb-2">{q.theme}</h3>
            <div className="space-y-2 flex-1">
              {q.features.map((feature, j) => (
                <div key={j} className="flex items-start gap-2">
                  <CheckCircle2 className={`w-3 h-3 ${colors.text} mt-0.5 flex-shrink-0`} />
                  <span className="text-gray-300 text-xs">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>

    <div className="mt-3 text-center">
      <span className="text-gray-500 text-xs">Roadmap subject to change based on customer feedback and market conditions</span>
    </div>
  </div>
);

// AI Roadmap Slide (NEW)
const AIRoadmapSlide = ({ slide }) => (
  <div className={`relative w-full h-full bg-gradient-to-br ${slide.backgroundGradient} flex flex-col p-6 overflow-hidden`}>
    <div className="absolute inset-0 opacity-5">
      <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '30px 30px' }} />
    </div>
    <div className="text-center mb-4">
      <Badge className="bg-purple-500/20 text-purple-400 border-none mb-2">Shannon-Inspired</Badge>
      <h2 className="text-3xl font-bold text-white mb-1">{slide.title}</h2>
      <p className="text-purple-300 text-sm">{slide.subtitle}</p>
      <p className="text-gray-400 text-xs mt-1 max-w-2xl mx-auto">{slide.description}</p>
    </div>

    <div className="flex-1 grid grid-cols-4 gap-3">
      {slide.phases.map((phase, i) => {
        const colors = getColorClasses(phase.color);
        const Icon = phase.icon;
        return (
          <div key={i} className={`${colors.light} ${colors.border} border rounded-xl p-4 flex flex-col items-center text-center relative`}>
            <Badge className={`${colors.bg} ${colors.text} mb-2`}>{phase.version}</Badge>
            <div className={`p-3 ${colors.bg} rounded-xl mb-2`}>
              <Icon className={`w-8 h-8 ${colors.text}`} />
            </div>
            <h3 className="text-white font-semibold text-sm mb-1">{phase.name}</h3>
            <p className="text-gray-400 text-xs leading-snug mb-2">{phase.description}</p>
            <Badge variant="outline" className="border-white/20 text-gray-300 text-xs mt-auto">{phase.target}</Badge>
          </div>
        );
      })}
    </div>

    <div className="mt-3 text-center">
      <span className="text-gray-500 text-xs">Reference: </span>
      <span className="text-purple-400 text-xs font-mono">{slide.shannonRef}</span>
      <span className="text-gray-600 text-xs"> — 96.15% autonomous pentest success rate</span>
    </div>
  </div>
);

// Pricing Slide (NEW)
const PricingSlide = ({ slide }) => (
  <div className={`relative w-full h-full bg-gradient-to-br ${slide.backgroundGradient} flex flex-col p-6 overflow-hidden`}>
    <div className="absolute inset-0 opacity-10">
      <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
    </div>
    <div className="text-center mb-4">
      <h2 className="text-3xl font-bold text-white mb-1">{slide.title}</h2>
      <p className="text-blue-300">{slide.subtitle}</p>
    </div>
    <div className="flex-1 grid grid-cols-4 gap-4 items-stretch">
      {slide.tiers.map((tier, i) => {
        const colors = getColorClasses(tier.color);
        return (
          <div key={i} className={`relative flex flex-col bg-white/5 backdrop-blur-sm rounded-xl p-5 border ${tier.highlight ? 'border-purple-500 ring-2 ring-purple-500/30' : 'border-white/10'}`}>
            {tier.highlight && (
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-500 text-white">Most Popular</Badge>
            )}
            <h3 className={`text-xl font-bold ${colors.text} mb-1`}>{tier.name}</h3>
            <div className="mb-3">
              <span className="text-3xl font-bold text-white">{tier.price}</span>
              <span className="text-gray-400 text-sm">{tier.period}</span>
            </div>
            <Badge variant="outline" className="border-white/20 text-gray-300 w-fit mb-3">{tier.users}</Badge>
            <div className="space-y-2 flex-1">
              {tier.modules.map((mod, j) => (
                <div key={j} className="flex items-center gap-2">
                  <CheckCircle2 className={`w-4 h-4 ${colors.text} flex-shrink-0`} />
                  <span className="text-gray-300 text-sm">{mod}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// DDoS Live Slide
const DDoSLiveSlide = ({ slide }) => {
  const [attacks] = useState([
    { id: 1, type: 'SYN Flood', source: '45.33.32.156', target: 'api.example.com', pps: '2.4M', status: 'Mitigating', country: 'CN' },
    { id: 2, type: 'UDP Amplification', source: '185.220.101.35', target: 'cdn.example.com', pps: '890K', status: 'Blocked', country: 'RU' },
    { id: 3, type: 'HTTP Flood', source: '103.224.182.251', target: 'www.example.com', pps: '450K', status: 'Active', country: 'VN' },
    { id: 4, type: 'DNS Amplification', source: '94.102.49.193', target: 'dns.example.com', pps: '1.2M', status: 'Mitigating', country: 'UA' }
  ]);
  const [metrics] = useState({ totalAttacks: 847, mitigated: 812, peakTraffic: '45.2 Gbps', avgResponse: '< 50ms' });

  return (
    <div className={`relative w-full h-full bg-gradient-to-br ${slide.backgroundGradient} flex flex-col p-6 overflow-hidden`}>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '30px 30px' }} />
      </div>
      <div className="text-center mb-4">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Wifi className="w-8 h-8 text-red-400 animate-pulse" />
          <h2 className="text-3xl font-bold text-white">{slide.title}</h2>
        </div>
        <p className="text-gray-300">{slide.subtitle}</p>
      </div>
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="bg-white/10 rounded-xl p-3 text-center backdrop-blur-sm">
          <Activity className="w-6 h-6 text-red-400 mx-auto mb-1" />
          <p className="text-2xl font-bold text-white">{metrics.totalAttacks}</p>
          <p className="text-xs text-gray-400">Total Attacks (24h)</p>
        </div>
        <div className="bg-white/10 rounded-xl p-3 text-center backdrop-blur-sm">
          <Shield className="w-6 h-6 text-green-400 mx-auto mb-1" />
          <p className="text-2xl font-bold text-green-400">{metrics.mitigated}</p>
          <p className="text-xs text-gray-400">Mitigated</p>
        </div>
        <div className="bg-white/10 rounded-xl p-3 text-center backdrop-blur-sm">
          <TrendingUp className="w-6 h-6 text-orange-400 mx-auto mb-1" />
          <p className="text-2xl font-bold text-white">{metrics.peakTraffic}</p>
          <p className="text-xs text-gray-400">Peak Traffic</p>
        </div>
        <div className="bg-white/10 rounded-xl p-3 text-center backdrop-blur-sm">
          <Zap className="w-6 h-6 text-cyan-400 mx-auto mb-1" />
          <p className="text-2xl font-bold text-cyan-400">{metrics.avgResponse}</p>
          <p className="text-xs text-gray-400">Avg Response</p>
        </div>
      </div>
      <div className="flex-1 bg-slate-900/80 rounded-xl border border-red-500/30 p-4 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Radio className="w-4 h-4 text-red-400 animate-pulse" />
            Live Attack Feed
          </h3>
          <Badge className="bg-red-500/20 text-red-400 animate-pulse">● LIVE</Badge>
        </div>
        <div className="space-y-2">
          {attacks.map((attack) => (
            <div key={attack.id} className="flex items-center justify-between bg-white/5 rounded-lg p-3 hover:bg-white/10 transition-colors">
              <div className="flex items-center gap-4">
                <Badge className={`${attack.status === 'Active' ? 'bg-red-500/20 text-red-400' : attack.status === 'Mitigating' ? 'bg-orange-500/20 text-orange-400' : 'bg-green-500/20 text-green-400'}`}>
                  {attack.status}
                </Badge>
                <div>
                  <span className="text-white font-medium">{attack.type}</span>
                  <span className="text-gray-500 mx-2">→</span>
                  <span className="text-cyan-400 font-mono text-sm">{attack.target}</span>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <span className="text-gray-400 font-mono text-sm">{attack.source}</span>
                <Badge variant="outline" className="border-white/20 text-gray-300">{attack.country}</Badge>
                <span className="text-orange-400 font-bold">{attack.pps} PPS</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Use Cases Slide
const UseCasesSlide = ({ slide }) => (
  <div className={`relative w-full h-full bg-gradient-to-br ${slide.backgroundGradient} flex flex-col p-6 overflow-hidden`}>
    <div className="text-center mb-6">
      <h2 className="text-4xl font-bold text-white mb-2">{slide.title}</h2>
      <p className="text-purple-300">{slide.subtitle}</p>
    </div>
    <div className="flex-1 grid grid-cols-5 gap-4">
      {slide.useCases.map((useCase, i) => {
        const Icon = useCase.icon;
        return (
          <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 flex flex-col items-center text-center hover:bg-white/10 transition-colors">
            <div className="p-3 bg-purple-500/20 rounded-xl mb-3">
              <Icon className="w-8 h-8 text-purple-400" />
            </div>
            <h3 className="text-white font-semibold mb-2 text-sm">{useCase.title}</h3>
            <p className="text-gray-400 text-xs">{useCase.description}</p>
          </div>
        );
      })}
    </div>
  </div>
);

// USPCF Slide
const USPCFSlide = ({ slide }) => (
  <div className={`relative w-full h-full bg-gradient-to-br ${slide.backgroundGradient} flex flex-col p-6 overflow-hidden`}>
    <div className="text-center mb-4">
      <Badge className="bg-amber-500/20 text-amber-400 border-none mb-2">Patent Pending</Badge>
      <h2 className="text-4xl font-bold text-white mb-2">{slide.title}</h2>
      <p className="text-orange-300">{slide.subtitle}</p>
      <p className="text-gray-400 text-sm mt-2 max-w-2xl mx-auto">{slide.description}</p>
    </div>
    <div className="flex-1 grid grid-cols-2 gap-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <Layers className="w-5 h-5 text-amber-400" />
          11 Security Domains
        </h3>
        <div className="grid grid-cols-2 gap-2">
          {slide.domains.map((domain, i) => (
            <div key={i} className="flex items-center gap-2 p-2 bg-white/5 rounded-lg">
              <div className="w-2 h-2 bg-amber-400 rounded-full" />
              <span className="text-sm text-gray-300">{domain}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <Target className="w-5 h-5 text-orange-400" />
          Framework Mappings
        </h3>
        <div className="flex flex-wrap gap-2 mb-4">
          {slide.mappings.map((mapping, i) => (
            <Badge key={i} className="bg-orange-500/20 text-orange-300 border-orange-500/30">{mapping}</Badge>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="bg-white/10 rounded-xl p-4 text-center">
            <span className="text-3xl font-bold text-white">{slide.stats.controls}</span>
            <p className="text-xs text-gray-400 mt-1">Controls</p>
          </div>
          <div className="bg-white/10 rounded-xl p-4 text-center">
            <span className="text-3xl font-bold text-white">{slide.stats.domains}</span>
            <p className="text-xs text-gray-400 mt-1">Domains</p>
          </div>
          <div className="bg-white/10 rounded-xl p-4 text-center">
            <span className="text-3xl font-bold text-white">{slide.stats.mappings}</span>
            <p className="text-xs text-gray-400 mt-1">Mappings</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Frameworks Grid Slide
const FrameworksGridSlide = ({ slide }) => (
  <div className={`relative w-full h-full bg-gradient-to-br ${slide.backgroundGradient} flex flex-col p-4 overflow-hidden`}>
    <div className="text-center mb-3">
      <h2 className="text-3xl font-bold text-white mb-1">{slide.title}</h2>
      <p className="text-indigo-300">{slide.subtitle}</p>
    </div>
    <div className="flex-1 grid grid-cols-3 gap-2">
      {slide.frameworkGroups.map((group, i) => {
        const colors = getColorClasses(group.color);
        const Icon = group.icon;
        return (
          <div key={i} className={`${colors.light} ${colors.border} border rounded-lg p-3`}>
            <div className="flex items-center gap-2 mb-2">
              <Icon className={`w-4 h-4 ${colors.text}`} />
              <h3 className={`text-sm font-semibold ${colors.text}`}>{group.category}</h3>
            </div>
            <div className="flex flex-wrap gap-1">
              {group.frameworks.map((fw, j) => (
                <Badge key={j} variant="outline" className="border-white/20 text-white text-xs py-0">{fw}</Badge>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// Capabilities Slide
const CapabilitiesSlide = ({ slide }) => (
  <div className={`relative w-full h-full bg-gradient-to-br ${slide.backgroundGradient} flex flex-col p-6 overflow-hidden`}>
    <div className="text-center mb-6">
      <h2 className="text-4xl font-bold text-white mb-2">{slide.title}</h2>
      <p className="text-indigo-300">{slide.subtitle}</p>
    </div>
    <div className="flex-1 grid grid-cols-4 gap-4">
      {slide.capabilities.map((cap, i) => {
        const Icon = cap.icon;
        return (
          <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 flex flex-col items-center justify-center text-center">
            <Icon className="w-10 h-10 text-blue-400 mb-3" />
            <span className="text-5xl font-bold text-white mb-2">{cap.value}</span>
            <h3 className="text-lg font-semibold text-blue-300 mb-2">{cap.label}</h3>
            <p className="text-sm text-gray-400">{cap.description}</p>
          </div>
        );
      })}
    </div>
  </div>
);

// Contact Slide
const ContactSlide = ({ slide }) => (
  <div className={`relative w-full h-full bg-gradient-to-br ${slide.backgroundGradient} flex flex-col items-center justify-center p-8 overflow-hidden`}>
    <div className="absolute inset-0 opacity-10">
      <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
    </div>
    <h2 className="text-5xl font-bold text-white mb-4 text-center">{slide.title}</h2>
    <p className="text-xl text-purple-300 mb-6">{slide.subtitle}</p>
    <p className="text-gray-300 max-w-2xl text-center mb-6">{slide.description}</p>
    <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-6 py-3 mb-6">
      <Mail className="w-5 h-5 text-blue-400" />
      <span className="text-xl text-white">{slide.email}</span>
    </div>
    <div className="flex gap-3 flex-wrap justify-center mb-4">
      {slide.benefits.map((benefit, i) => (
        <div key={i} className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg">
          <CheckCircle2 className="w-4 h-4 text-green-400" />
          <span className="text-sm text-gray-300">{benefit}</span>
        </div>
      ))}
    </div>
    <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30 text-sm mb-4">{slide.version}</Badge>
    <p className="text-amber-400 font-semibold text-lg italic">{slide.tagline}</p>
  </div>
);

// ── Slide Renderer ─────────────────────────────────────────────────────
// Problem Slide
const ProblemSlide = ({ slide }) => (
  <div className={`relative w-full h-full bg-gradient-to-br ${slide.backgroundGradient} flex flex-col p-8 overflow-hidden`}>
    <div className="text-center mb-6">
      <h2 className="text-3xl font-bold text-white mb-1">{slide.title}</h2>
      <p className="text-lg text-rose-300">{slide.subtitle}</p>
    </div>
    <div className="grid grid-cols-3 gap-4 flex-1">
      {slide.problems.map((p, i) => {
        const colors = getColorClasses(p.color);
        return (
          <div key={i} className={`${colors.light} border ${colors.border} rounded-xl p-5 flex flex-col items-center justify-center text-center backdrop-blur-sm`}>
            <p.icon className={`w-8 h-8 ${colors.text} mb-2`} />
            <span className={`text-3xl font-bold ${colors.text}`}>{p.stat}</span>
            <span className="text-white text-sm mt-1 font-medium">{p.label}</span>
            <span className="text-gray-500 text-xs mt-1">{p.source}</span>
          </div>
        );
      })}
    </div>
  </div>
);

// DPDPA Detail Slide
const DPDPADetailSlide = ({ slide }) => (
  <div className={`relative w-full h-full bg-gradient-to-br ${slide.backgroundGradient} flex flex-col p-6 overflow-hidden`}>
    <div className="text-center mb-3">
      <div className="inline-flex items-center gap-2 mb-2 px-4 py-1.5 bg-rose-500/20 border border-rose-500/30 rounded-full">
        <Fingerprint className="w-4 h-4 text-rose-400" />
        <span className="text-rose-300 text-sm font-medium">NEW MODULE</span>
      </div>
      <h2 className="text-3xl font-bold text-white mb-1">{slide.title}</h2>
      <p className="text-lg text-rose-300">{slide.subtitle}</p>
      <p className="text-gray-400 mt-1 text-sm max-w-3xl mx-auto">{slide.description}</p>
    </div>
    <div className="grid grid-cols-3 gap-3 flex-1 mb-3">
      {slide.features.map((f, i) => {
        const colors = getColorClasses(f.color);
        return (
          <div key={i} className={`${colors.light} border ${colors.border} rounded-lg p-3 backdrop-blur-sm`}>
            <div className="flex items-center gap-2 mb-1">
              <f.icon className={`w-4 h-4 ${colors.text}`} />
              <h3 className="text-white text-sm font-semibold">{f.title}</h3>
            </div>
            <p className="text-gray-400 text-xs">{f.description}</p>
          </div>
        );
      })}
    </div>
    <div className="flex justify-center gap-8">
      {slide.stats.map((s, i) => (
        <div key={i} className="text-center">
          <span className="text-2xl font-bold text-rose-400">{s.value}</span>
          <p className="text-gray-400 text-xs">{s.label}</p>
        </div>
      ))}
    </div>
  </div>
);

// PII Scanner Slide
const PIIScannerSlide = ({ slide }) => (
  <div className={`relative w-full h-full bg-gradient-to-br ${slide.backgroundGradient} flex flex-col p-6 overflow-hidden`}>
    <div className="text-center mb-4">
      <h2 className="text-3xl font-bold text-white mb-1">{slide.title}</h2>
      <p className="text-lg text-emerald-300">{slide.subtitle}</p>
      <p className="text-gray-400 mt-1 text-sm max-w-3xl mx-auto">{slide.description}</p>
    </div>
    <div className="grid grid-cols-2 gap-4 flex-1">
      <div className="space-y-3">
        <h3 className="text-white font-semibold text-sm mb-2 flex items-center gap-2"><Database className="w-4 h-4 text-emerald-400" /> Supported Databases</h3>
        {slide.databases.map((db, i) => {
          const colors = getColorClasses(db.color);
          return (
            <div key={i} className={`${colors.light} border ${colors.border} rounded-lg p-3`}>
              <span className={`font-semibold ${colors.text}`}>{db.name}</span>
              <span className="text-gray-400 text-xs ml-2">{db.cloud}</span>
            </div>
          );
        })}
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mt-2">
          <h4 className="text-amber-400 text-sm font-semibold">{slide.complianceCheck.title}</h4>
          <p className="text-gray-400 text-xs mt-1">{slide.complianceCheck.description}</p>
        </div>
      </div>
      <div>
        <h3 className="text-white font-semibold text-sm mb-2 flex items-center gap-2"><Search className="w-4 h-4 text-emerald-400" /> 14 PII Categories</h3>
        <div className="flex flex-wrap gap-2">
          {slide.piiCategories.map((cat, i) => (
            <span key={i} className="px-2.5 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-md text-emerald-300 text-xs">{cat}</span>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// Breach Tracker Slide
const BreachTrackerSlide = ({ slide }) => (
  <div className={`relative w-full h-full bg-gradient-to-br ${slide.backgroundGradient} flex flex-col p-6 overflow-hidden`}>
    <div className="text-center mb-4">
      <h2 className="text-3xl font-bold text-white mb-1">{slide.title}</h2>
      <p className="text-lg text-red-300">{slide.subtitle}</p>
      <p className="text-gray-400 mt-1 text-sm max-w-3xl mx-auto">{slide.description}</p>
    </div>
    <div className="grid grid-cols-2 gap-4 flex-1">
      <div>
        <h3 className="text-white font-semibold text-sm mb-2 flex items-center gap-2"><Clock className="w-4 h-4 text-red-400" /> Countdown Levels</h3>
        <div className="space-y-2">
          {slide.countdownLevels.map((l, i) => {
            const colors = getColorClasses(l.color);
            return (
              <div key={i} className={`${colors.light} border ${colors.border} rounded-lg p-2.5 flex items-center gap-3`}>
                <span className={`font-bold ${colors.text} text-sm min-w-[70px]`}>{l.status}</span>
                <span className="text-gray-400 text-xs min-w-[50px]">{l.hours}</span>
                <span className="text-gray-500 text-xs">{l.description}</span>
              </div>
            );
          })}
        </div>
        <h3 className="text-white font-semibold text-sm mt-3 mb-2">Status Workflow</h3>
        <div className="flex flex-wrap gap-1">
          {slide.workflow.map((s, i) => (
            <React.Fragment key={i}>
              <span className="px-2 py-0.5 bg-white/10 rounded text-white text-xs">{s}</span>
              {i < slide.workflow.length - 1 && <ArrowRight className="w-3 h-3 text-gray-600" />}
            </React.Fragment>
          ))}
        </div>
      </div>
      <div>
        <h3 className="text-white font-semibold text-sm mb-2 flex items-center gap-2"><FileText className="w-4 h-4 text-red-400" /> Auto-Generated Letters</h3>
        {slide.letters.map((l, i) => (
          <div key={i} className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-3">
            <div className="flex items-center gap-2 mb-1">
              <Download className="w-4 h-4 text-red-400" />
              <span className="text-white font-semibold text-sm">{l.name}</span>
              <Badge className="bg-red-500/20 text-red-300 text-xs border-0">{l.format}</Badge>
            </div>
            <p className="text-gray-400 text-xs">{l.description}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Why Beskar (Competitive) Slide
const WhyBeskarSlide = ({ slide }) => (
  <div className={`relative w-full h-full bg-gradient-to-br ${slide.backgroundGradient} flex flex-col p-6 overflow-hidden`}>
    <div className="text-center mb-4">
      <h2 className="text-3xl font-bold text-white mb-1">{slide.title}</h2>
      <p className="text-lg text-violet-300">{slide.subtitle}</p>
    </div>
    <div className="flex-1 overflow-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/10">
            <th className="text-left p-2 text-gray-400 text-xs font-medium">Category</th>
            <th className="text-left p-2 text-violet-400 text-xs font-medium">Beskar</th>
            <th className="text-left p-2 text-gray-500 text-xs font-medium">Typical Alternatives</th>
            <th className="text-left p-2 text-emerald-400 text-xs font-medium">Advantage</th>
          </tr>
        </thead>
        <tbody>
          {slide.comparisons.map((c, i) => (
            <tr key={i} className="border-b border-white/5 hover:bg-white/5 transition-colors">
              <td className="p-2 text-white font-medium text-xs">{c.category}</td>
              <td className="p-2 text-violet-300 text-xs">{c.beskar}</td>
              <td className="p-2 text-gray-500 text-xs">{c.others}</td>
              <td className="p-2"><Badge className="bg-emerald-500/20 text-emerald-300 text-xs border-0">{c.advantage}</Badge></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// ROI Value Slide
const ROIValueSlide = ({ slide }) => (
  <div className={`relative w-full h-full bg-gradient-to-br ${slide.backgroundGradient} flex flex-col p-6 overflow-hidden`}>
    <div className="text-center mb-4">
      <h2 className="text-3xl font-bold text-white mb-1">{slide.title}</h2>
      <p className="text-lg text-emerald-300">{slide.subtitle}</p>
    </div>
    <div className="grid grid-cols-3 gap-3 flex-1">
      {slide.metrics.map((m, i) => {
        const colors = getColorClasses(m.color);
        return (
          <div key={i} className={`${colors.light} border ${colors.border} rounded-xl p-4 flex flex-col justify-center backdrop-blur-sm`}>
            <m.icon className={`w-6 h-6 ${colors.text} mb-2`} />
            <div className="text-xs text-gray-500 line-through mb-0.5">{m.before}</div>
            <div className="text-sm text-white font-semibold mb-1">{m.after}</div>
            <Badge className={`${colors.bg} ${colors.text} text-xs border-0 w-fit`}>{m.savings}</Badge>
          </div>
        );
      })}
    </div>
  </div>
);

const renderSlide = (slide) => {
  switch (slide.type) {
    case 'title': return <TitleSlide slide={slide} />;
    case 'problem-slide': return <ProblemSlide slide={slide} />;
    case 'platform-overview': return <PlatformOverviewSlide slide={slide} />;
    case 'grc-detail':
    case 'osint-detail':
    case 'litesabre-detail':
    case 'cspm-detail': return <GRCDetailSlide slide={slide} />;
    case 'demo-screenshot': return <DemoScreenshotSlide slide={slide} />;
    case 'risk-audit': return <RiskAuditSlide slide={slide} />;
    case 'litesabre-pro-detail': return <LiteSabreProSlide slide={slide} />;
    case 'ai-roadmap': return <AIRoadmapSlide slide={slide} />;
    case 'extended-roadmap': return <ExtendedRoadmapSlide slide={slide} />;
    case 'ddos-live': return <DDoSLiveSlide slide={slide} />;
    case 'use-cases': return <UseCasesSlide slide={slide} />;
    case 'dpdpa-detail': return <DPDPADetailSlide slide={slide} />;
    case 'pii-scanner-detail': return <PIIScannerSlide slide={slide} />;
    case 'breach-tracker-detail': return <BreachTrackerSlide slide={slide} />;
    case 'why-beskar': return <WhyBeskarSlide slide={slide} />;
    case 'roi-value': return <ROIValueSlide slide={slide} />;
    case 'uspcf': return <USPCFSlide slide={slide} />;
    case 'frameworks-grid': return <FrameworksGridSlide slide={slide} />;
    case 'capabilities': return <CapabilitiesSlide slide={slide} />;
    case 'contact': return <ContactSlide slide={slide} />;
    default: return <TitleSlide slide={slide} />;
  }
};

// ── Main Component ─────────────────────────────────────────────────────
const MarketingDemoPPT = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isAutoPlay, setIsAutoPlay] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const slideRef = useRef(null);
  const autoPlayRef = useRef(null);

  const totalSlides = PRESENTATION_SLIDES.length;

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % totalSlides);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  const goToSlide = (index) => setCurrentSlide(index);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') nextSlide();
      else if (e.key === 'ArrowLeft') prevSlide();
      else if (e.key === 'Escape') setIsFullscreen(false);
      else if (e.key === 'f' || e.key === 'F') setIsFullscreen(!isFullscreen);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  // Auto-play
  useEffect(() => {
    if (isAutoPlay) {
      autoPlayRef.current = setInterval(() => setCurrentSlide((prev) => (prev + 1) % totalSlides), 6000);
    } else if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
    return () => { if (autoPlayRef.current) clearInterval(autoPlayRef.current); };
  }, [isAutoPlay, totalSlides]);

  // Export to PDF
  const exportToPDF = async () => {
    setIsExporting(true);
    toast.info('Generating complete PDF presentation...');
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/marketing/download-pdf`);
      if (!response.ok) throw new Error('Failed to generate PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Beskar-IT-Marketing-Presentation.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const currentSlideData = PRESENTATION_SLIDES[currentSlide];

  return (
    <div className={`${isFullscreen ? 'fixed inset-0 z-50 bg-black' : 'p-6'}`}>
      {!isFullscreen && (
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Presentation className="w-8 h-8 text-blue-500" />
                Marketing Demo Presentation
              </h1>
              <p className="text-muted-foreground mt-1">Beskar IT v3.0.0 — Security. Privacy. Compliance. One platform.</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => navigate('/apps')}>
                <Home className="w-4 h-4 mr-2" />Back to Home
              </Button>
              <Button variant="outline" size="sm" onClick={() => setIsAutoPlay(!isAutoPlay)} className={isAutoPlay ? 'bg-green-500/10 border-green-500/30' : ''}>
                {isAutoPlay ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                {isAutoPlay ? 'Stop' : 'Auto Play'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setIsFullscreen(true)}>
                <Maximize2 className="w-4 h-4 mr-2" />Present
              </Button>
              <Button size="sm" onClick={exportToPDF} disabled={isExporting} className="bg-blue-600 hover:bg-blue-700">
                <Download className="w-4 h-4 mr-2" />{isExporting ? 'Generating...' : 'Download Full PDF'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className={`${isFullscreen ? 'h-full' : ''}`}>
        <div className={`relative ${isFullscreen ? 'h-full' : 'aspect-video'} bg-black rounded-xl overflow-hidden shadow-2xl`}>
          <div ref={slideRef} className="w-full h-full">{renderSlide(currentSlideData)}</div>

          <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
            <div className="flex items-center justify-between">
              <div className="flex-1 mx-4"><Progress value={((currentSlide + 1) / totalSlides) * 100} className="h-1" /></div>
              <div className="flex items-center gap-4 text-white">
                <Button variant="ghost" size="icon" onClick={prevSlide} className="text-white hover:bg-white/20"><ChevronLeft className="w-6 h-6" /></Button>
                <span className="font-medium min-w-[80px] text-center">{currentSlide + 1} / {totalSlides}</span>
                <Button variant="ghost" size="icon" onClick={nextSlide} className="text-white hover:bg-white/20"><ChevronRight className="w-6 h-6" /></Button>
                {isFullscreen && (
                  <Button variant="ghost" size="icon" onClick={() => setIsFullscreen(false)} className="text-white hover:bg-white/20 ml-4"><Minimize2 className="w-5 h-5" /></Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {!isFullscreen && (
          <>
            <div className="mt-6">
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Slides Overview ({totalSlides} slides)</h3>
              <ScrollArea className="w-full">
                <div className="flex gap-2 pb-2">
                  {PRESENTATION_SLIDES.map((slide, index) => (
                    <button key={slide.id} onClick={() => goToSlide(index)}
                      className={`flex-shrink-0 w-32 h-20 rounded-lg overflow-hidden border-2 transition-all relative ${currentSlide === index ? 'border-blue-500 ring-2 ring-blue-500/30' : 'border-border hover:border-blue-500/50'}`}>
                      <div className={`absolute inset-0 bg-gradient-to-br ${slide.backgroundGradient} flex items-center justify-center`}>
                        <span className="text-white text-xs font-medium px-1 text-center leading-tight">{index + 1}. {slide.title?.substring(0, 18)}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </div>

            <Card className="mt-4">
              <CardContent className="py-3">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-6">
                    <span className="text-muted-foreground"><kbd className="px-2 py-1 bg-muted rounded text-xs">←</kbd> / <kbd className="px-2 py-1 bg-muted rounded text-xs ml-1">→</kbd> Navigate</span>
                    <span className="text-muted-foreground"><kbd className="px-2 py-1 bg-muted rounded text-xs">F</kbd> Fullscreen</span>
                    <span className="text-muted-foreground"><kbd className="px-2 py-1 bg-muted rounded text-xs">Esc</kbd> Exit</span>
                  </div>
                  <Badge variant="outline" className="text-blue-500 border-blue-500/30">{totalSlides} Slides</Badge>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default MarketingDemoPPT;
